export class BankColumns {
  private columnFieldDefinitions: Array<{ text: string; key: string; kind: "normal" | "concatenate" | "ignore" }> = []
  private totalNumberOfHeaderLinesToIgnore: number = 1
  private mappedColumnList: Array<{ text: string; value: string }> = []

  static createFromFormat(format: {
    fields: Array<{ text: string; key: string; kind: "normal" | "concatenate" | "ignore" }>,
    header_lines: number
  }): BankColumns {
    const instance = new BankColumns()
    instance.setFromFormat(format)
    return instance
  }

  public validate(): string[] {
    const errors: string[] = []

    const normalFieldDefinitions = this.columnFieldDefinitions.filter(fieldDefinition => fieldDefinition.kind === "normal")
    if (normalFieldDefinitions.length === 0) {
      errors.push("Debe haber al menos una columna normal.")
    }

    const allFieldKeys = this.columnFieldDefinitions.map(fieldDefinition => fieldDefinition.key)
    const duplicateKeys = allFieldKeys.filter((fieldKey, index) => allFieldKeys.indexOf(fieldKey) !== index)
    if (duplicateKeys.length > 0) {
      const uniqueDuplicateKeys = Array.from(new Set(duplicateKeys))
      errors.push(`Hay claves duplicadas: ${uniqueDuplicateKeys.join(", ")}`)
    }

    const hasConcatenateField = this.columnFieldDefinitions.some(fieldDefinition => fieldDefinition.kind === "concatenate")
    if (hasConcatenateField) {
      const hasConceptFieldByKey = this.columnFieldDefinitions.some(
        fieldDefinition => fieldDefinition.kind === "normal" && /^concept(o)?$/i.test(fieldDefinition.key)
      )
      const hasConceptFieldByText = this.columnFieldDefinitions.some(
        fieldDefinition => fieldDefinition.kind === "normal" && /concept/i.test(fieldDefinition.text)
      )
      if (!hasConceptFieldByKey && !hasConceptFieldByText) {
        errors.push("Para concatenar, debe existir una columna de Concepto.")
      }
    }

    return errors
  }

  toView(): {
    columns: Array<{ text: string; value: string }>
    numberOfHeaderLinesToIgnore: number
  } {
    return {
      columns: this.mappedColumnList.slice(),
      numberOfHeaderLinesToIgnore: this.totalNumberOfHeaderLinesToIgnore,
    }
  }

  toFormat(): {
    fields: Array<{ text: string; key: string; kind: "normal" | "concatenate" | "ignore" }>
    header_lines: number
  } {
 
    const clonedFieldDefinitions = JSON.parse(JSON.stringify(this.columnFieldDefinitions))

    let ignoreCounter = 1
    clonedFieldDefinitions
      .filter(field => /^ignorar_/i.test(field.key))
      .forEach(field => {
        field.key = `ignorar_${ignoreCounter}`
        field.text = "Ignorar"
        ignoreCounter++
      })

    let concatenateCounter = 1
    clonedFieldDefinitions
      .filter(field => /^concatenar_/i.test(field.key))
      .forEach(field => {
        field.key = `concatenar_${concatenateCounter}`
        field.text = "Concatenar"
        concatenateCounter++
      })

    return {
      fields: clonedFieldDefinitions,
      header_lines: this.totalNumberOfHeaderLinesToIgnore,
    }
  }


  setHeaderLinesToIgnore(headerLineCount: number): void {
    const parsedHeaderLineCount = Number(headerLineCount)
    this.totalNumberOfHeaderLinesToIgnore = isNaN(parsedHeaderLineCount) || parsedHeaderLineCount < 0 ? 0 : parsedHeaderLineCount
  }

  addIgnoreColumn(): void {
    const existingIgnoreColumns = this.mappedColumnList.filter(column => /^ignorar_/i.test(column.value)).length
    const nextIgnoreIndex = existingIgnoreColumns + 1
    const ignoreKey = `ignorar_${nextIgnoreIndex}`
    const ignoreText = "Ignorar"

    this.mappedColumnList.push({ text: ignoreText, value: ignoreKey })

    this.columnFieldDefinitions.push({
      key: ignoreKey,
      text: ignoreText,
      kind: "ignore",
    })
  }

  addConcatenateColumn(): void {
    const existingConcatenateColumns = this.mappedColumnList.filter(column => /^concatenar_/i.test(column.value)).length
    const nextConcatenateIndex = existingConcatenateColumns + 1
    const concatenateKey = `concatenar_${nextConcatenateIndex}`
    const concatenateText = "Concatenar"

    this.mappedColumnList.push({ text: concatenateText, value: concatenateKey })

    this.columnFieldDefinitions.push({
      key: concatenateKey,
      text: concatenateText,
      kind: "concatenate",
    })
  }

  canRemoveColumn(column: { text: string; value: string }): boolean {
    return /^ignorar_/i.test(column.value) || /^concatenar_/i.test(column.value)
  }

  removeColumn(column: { text: string; value: string }): void {
    if (!this.canRemoveColumn(column)) return

    this.mappedColumnList = this.mappedColumnList.filter(c => c.value !== column.value)

    this.columnFieldDefinitions = this.columnFieldDefinitions.filter(f => f.key !== column.value)
  }

  setOrder(desiredOrderValues: string[]): void {
    const allFieldKeysSet = new Set(this.columnFieldDefinitions.map(fieldDefinition => fieldDefinition.key))
    const reorderedFieldDefinitions: typeof this.columnFieldDefinitions = []
    const visitedOrderValuesSet = new Set<string>()

    desiredOrderValues.forEach(orderValue => {
      if (allFieldKeysSet.has(orderValue)) {
        const fieldDefinition = this.columnFieldDefinitions.find(fieldDefinition => fieldDefinition.key === orderValue)!
        reorderedFieldDefinitions.push(fieldDefinition)
      }
      visitedOrderValuesSet.add(orderValue)
    })

    this.columnFieldDefinitions.forEach(fieldDefinition => {
      if (!visitedOrderValuesSet.has(fieldDefinition.key)) {
        reorderedFieldDefinitions.push(fieldDefinition)
      }
    })
    this.columnFieldDefinitions = reorderedFieldDefinitions

    const allMappedValues = this.mappedColumnList.map(mappedColumn => mappedColumn.value)
    const missingOrderValues = allMappedValues.filter(mappedValue => !visitedOrderValuesSet.has(mappedValue))
    const finalOrderValues = [...desiredOrderValues, ...missingOrderValues]

    this.mappedColumnList = finalOrderValues.map((value) => {
      let baseText = ""
      const fieldDefinition = this.columnFieldDefinitions.find(fieldDefinition => fieldDefinition.key === value)

      if (fieldDefinition) {
        baseText = fieldDefinition.text
      } else if (/^ignorar_/i.test(value)) {
        baseText = "Ignorar"
      } else if (/^concatenar_/i.test(value)) {
        baseText = "Concatenar"
      } else {
        const originalMappedColumn = this.mappedColumnList.find(mappedColumn => mappedColumn.value === value)
        baseText = originalMappedColumn ? originalMappedColumn.text.replace(/^[A-Z]+\. /, "") : value
      }

      return { text: this.prefixWithLetter(baseText), value }
    })
  }

  private setFromFormat(formatInput: {
    fields?: Array<{ text: string; key: string; kind: "normal" | "concatenate" | "ignore" }>,
    header_lines?: number
  }): void {
    if (!Array.isArray(formatInput.fields)) {
      this.columnFieldDefinitions = []
      this.mappedColumnList = []
      this.totalNumberOfHeaderLinesToIgnore = 0
      return
    }

    this.columnFieldDefinitions = formatInput.fields.map(fieldDefinition => {
      if (/^concatenar_/i.test(fieldDefinition.key)) {
        return { ...fieldDefinition, text: "Concatenar" }
      }
      if (/^ignorar_/i.test(fieldDefinition.key)) {
        return { ...fieldDefinition, text: "Ignorar" }
      }
      return { ...fieldDefinition }
    })

    this.totalNumberOfHeaderLinesToIgnore = formatInput.header_lines ?? 0

    this.mappedColumnList = this.columnFieldDefinitions.map(fieldDefinition => ({
      text: this.prefixWithLetter(fieldDefinition.text),
      value: fieldDefinition.key,
    }))
  }

  private prefixWithLetter(text: string): string {
    return text
  }
}
